import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import RotatingLogo from './NathNagriLogo';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const dropdownRef = useRef();

  /* CLOSE DROPDOWN ON OUTSIDE CLICK */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'होम (Home)', path: '/' },
    { name: 'मंदिर (Temples)', path: '/temples' },
    { name: 'ब्लॉग (Blog)', path: '/blog' },
    { name: 'मंच (Forum)', path: '/forum' },
    { name: 'मतदान (Polls)', path: '/polls' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-sm border-b border-saffron-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">

          {/* LOGO */}
          <Link to="/" className="flex items-center space-x-2">
            <RotatingLogo size={60} />
            <span className="font-bold text-xl text-stone-800">
              नाथ नगरी <span className="text-saffron-600">बरेली</span>
            </span>
          </Link>

          {/* DESKTOP NAV */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`px-3 py-2 rounded-full text-sm ${isActive(link.path)
                  ? 'bg-saffron-50 text-saffron-700'
                  : 'text-stone-600 hover:bg-stone-50'
                  }`}
              >
                {link.name}
              </Link>
            ))}

            {user && (
              <Link
                to="/write"
                className="px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-full"
              >
                लिखें (Write)
              </Link>
            )}

            {/* USER DROPDOWN */}
            {user ? (
              <div ref={dropdownRef} className="relative ml-2">

                {/* BUTTON */}
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-stone-100"
                >
                  <User className="w-5 h-5 text-saffron-600" />
                  <span className="text-sm font-medium">
                    Welcome, {user.name}
                  </span>
                </button>

                {/* DROPDOWN */}
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border z-50"
                    >
                      <div className="p-3 border-b">
                        <p className="text-sm font-semibold">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>

                      <div className="p-2">
                        <p className="text-xs text-gray-400 px-2 mb-1">
                          Role: {user.role}
                        </p>

                        {isAdmin && (
                          <Link
                            to="/admin/dashboard"
                            onClick={() => setShowDropdown(false)}
                            className="block px-3 py-2 text-sm text-red-600 hover:bg-gray-100 rounded"
                          >
                            Admin Panel
                          </Link>
                        )}

                        <button
                          onClick={() => {
                            logout();
                            setShowDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded"
                        >
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to="/login"
                className="ml-2 flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-semibold shadow-md bg-saffron-600 text-white hover:bg-saffron-700 transition-colors"
              >
                <User className="w-4 h-4" />
                <span>Login / Sign Up</span>
              </Link>
            )}
          </div>

          {/* MOBILE BUTTON */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE NAV */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="p-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block py-2"
                >
                  {link.name}
                </Link>
              ))}

              {user ? (
                <>
                  <div className="border-t pt-2">
                    <p className="text-sm">Welcome, {user.name}</p>
                    <p className="text-xs text-gray-500">
                      Role: {user.role}
                    </p>
                  </div>

                  <button
                    onClick={logout}
                    className="block text-left w-full py-2 text-red-600"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login" className="block py-3 font-semibold text-saffron-600 border-t mt-2 border-stone-100">
                  Login / Sign Up
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;